import React, { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { configIntervalSelector } from '@store/timeline/config/selectors';
import {
  requestIntervalSelector,
  isFetchingSelector,
  actualTimelineIntervalSelector,
  timelineSummaryDataSelector,
  sessionStartEndSelector,
  timelineDataDatePointsSelector,
  sessionIDSelector,
} from '@store/timeline/data/selectors';
import { expandEventsSelector } from '@store/timeline/ui/selectors';
import {
  SESSION_TYPES,
  TIMELINE_DIMENSION,
} from '@components/session-timeline/utils/constants';
import { tempDashboardDataSelector } from '@store/timeline/tempDashboard/selectors';
import { generateDateRange } from '@utils/generateDateRange';
import EmptyPlaceholder from '@components/common/EmptyPlaceholder';
import usePageTitle from '@src/hooks/usePageTitle';
import { viewersBrowserTabFeatureSelector } from '@store/common/selectors';
import Summary from './components/Summary';
import SummaryDetail from './components/Summary/SummaryDetail';
import Toolbox from './components/Toolbox';
import DashboardTabs from '../common/Dashboard/DashboardTabs';
import TimelineEvent from './components/TimelineEvent';
import SessionDetail from './components/SessionDetail';
import {
  Header, Wrapper, Content, StyledDivider,
} from './styled';
import getGranularity from './utils/getGranularity';

export interface IProps {
  pageType: number;
  isSharing: boolean;
  id: string | number;
}

const Timeline = ({ isSharing, id, pageType }: IProps) => {
  const expandEvents = useSelector(expandEventsSelector);
  const isFetching = useSelector(isFetchingSelector);
  const [actualStart, actualEnd] = useSelector(requestIntervalSelector);
  const [sessionStart, sessionEnd] = useSelector(sessionStartEndSelector);
  const actualInterval = useSelector(actualTimelineIntervalSelector);
  const timelineSummary = useSelector(timelineSummaryDataSelector);
  const timelineDataDatePoints = useSelector(timelineDataDatePointsSelector);
  const dashboard = useSelector(tempDashboardDataSelector);
  const { startTime, endTime } = useSelector(configIntervalSelector);
  const start = actualStart || sessionStart;
  const end = actualEnd || sessionEnd;
  const sessionId = useSelector(sessionIDSelector);
  const vmBrowserTabEnabled = useSelector(viewersBrowserTabFeatureSelector);

  const rootDateRange = useMemo(
    () => {
      if (isFetching) {
        return [];
      }
      return generateDateRange({
        start, end, granularity: getGranularity(start, end), timelineDataDatePoints,
      });
    },
    [isFetching, start, end, timelineDataDatePoints],
  );

  const dateRange = useMemo(
    () => (
      isFetching ? [] : generateDateRange({
        start: startTime,
        end: endTime,
        granularity: getGranularity(startTime, endTime),
        timelineDataDatePoints,
      })),
    [isFetching, startTime, endTime, timelineDataDatePoints],
  );

  usePageTitle({ text: `Timeline - ${sessionId}` });

  return (
    <Wrapper>
      <Header>
        <DashboardTabs id={id} hidden={vmBrowserTabEnabled} />
        <Toolbox id={id} pageType={pageType} dashboard={dashboard} />
        {timelineSummary && <Summary />}
      </Header>
      <Header>
        {timelineSummary && <SummaryDetail />}
      </Header>
      <Content>
        {actualInterval ? (
          <>
            <TimelineEvent
              lightBorder={false}
              divided
              isSharing={isSharing}
              type={SESSION_TYPES.SESSION_OVERVIEW}
              label="Session Overview"
              dateRange={rootDateRange}
              noDataText="No Session Overview"
            />
            <StyledDivider className="header-line" />
            <SessionDetail isSharing={isSharing} dateRange={dateRange} />
            <StyledDivider className="sub-header-line" />
            {[
              TIMELINE_DIMENSION.Bitrate,
              // TIMELINE_DIMENSION.Framerate,
              TIMELINE_DIMENSION.CDN,
              TIMELINE_DIMENSION.Asset,
              TIMELINE_DIMENSION.Error,
            ].map(event => (
              <TimelineEvent
                isSharing={isSharing}
                key={event}
                label={event}
                expand={expandEvents.includes(event)}
                dateRange={dateRange}
                noDataText={`No ${event}`}
              />
            ))}
            <StyledDivider className="sub-header-line" />
            <TimelineEvent
              isSharing={isSharing}
              key={TIMELINE_DIMENSION.CustomEvent}
              label={TIMELINE_DIMENSION.CustomEvent}
              expand={expandEvents.includes(TIMELINE_DIMENSION.CustomEvent)}
              dateRange={dateRange}
              noDataText="No Custom Event"
            />
          </>
        ) : (
          <EmptyPlaceholder
            className=""
            emptyPlaceholder="No Data"
          />
        )}
      </Content>
    </Wrapper>
  );
};

export default Timeline;
